<template>
  <div>
    <div :class="containerClass" class="mb-5">
      <!-- Shared content container -->
      <div class="content-wrapper">
        <!-- Heading -->
        <div class="d-flex flex-column flex-sm-row align-items-sm-center mb-24pt header-section">
          <div class="d-flex align-items-center">
            <h2 class="mb-0">
              {{ title }}
              <i
                class="material-icons icon-16pt clickable-item"
                v-b-popover.hover.top="`View skill cards and perform interactive AI quizes.`"
                >info</i
              >
            </h2>
          </div>
          <div class="w-auto ml-sm-auto mb-sm-0 justify-content-end">
            <b-btn
              variant="secondary"
              :style="{
                width: isMobileScreen ? '100%' : '190px',
                marginTop: isMobileScreen ? '20px' : '',
                backgroundColor: get(getLoggedInUser, 'linked_entity.primary_color'),
                border: 'none',
              }"
              type="submit"
              class=" btn-normal"
              @click.prevent="goToCreateSkillCard"
            >
              <i class="material-icons mr-1">add</i>Create SkillCard
            </b-btn>
          </div>
        </div>

        <!-- Card Scroll Section -->
        <div class="card-scroll-area">
          <div class="cards-wrapper" ref="cardsWrapper" :style="{ gap: isMobileScreen ? '' : '8px' }">
            <card-item v-for="card in cards" :key="card.id" :card="card" @open-flashcard-modal="handleModalOpen" />
          </div>
        </div>
        <!-- Navigation Buttons -->
        <button
          class="nav-button nav-button-left"
          @click="scrollCards('prev')"
          :class="{ disabled: scrollPosition <= 0 }"
          :style="{ display: isMobileScreen ? 'none' : '' }"
        >
          <span class="material-icons icon-32pt">
            chevron_left
          </span>
        </button>

        <button
          class="nav-button nav-button-right"
          @click="scrollCards('next')"
          :class="{ disabled: isScrollEnd }"
          :style="{ display: isMobileScreen ? 'none' : '' }"
        >
          <span class="material-icons icon-32pt">
            chevron_right
          </span>
        </button>
      </div>
    </div>
    <flashcard-modal :is-open="openSkillsetModal" @hide-flashcard-modal="openSkillsetModal = false" />
  </div>
</template>

<script>
import CardItem from '@/components/SkillCard/CardItem.vue';
import Page from '@/components/Page.vue';
import { mapGetters } from 'vuex';
import { get } from 'lodash';
import FlashcardModal from '@/components/FlashcardModal/FlashcardModal.vue';

export default {
  name: 'SkillCardHome',
  components: {
    CardItem,
    FlashcardModal,
  },
  extends: Page,

  data() {
    return {
      scrollPosition: 0,
      isScrollEnd: false,
      scrollAmount: 300,
      title: 'Skill Cards',
      cards: [
        {
          id: 1,
          title: 'Skills Index',
          icon: 'import_contacts',
          description: 'Browse and like approved skill cards from other students',
          buttonText: 'Get Started',
          buttonEnabled: true,
          route: 'students',
        },
        {
          id: 2,
          title: 'Skill Cards Sets',
          icon: 'lightbulb_outline',
          description: 'Review concepts with interactive flashcards',
          buttonText: 'Start Learning',
          buttonEnabled: true,
          openModal: true,
          route: '#',
        },
        {
          id: 3,
          title: 'Skills Quiz',
          icon: 'assignment_turned_in',
          description: 'Test your knowledge with interactive quizzes',
          buttonText: 'Coming Soon',
          buttonEnabled: false,
          route: '#',
        },
        {
          id: 4,
          title: 'Sequence Challenge',
          icon: 'swap_vert',
          description: 'Arrange concepts in the correct order',
          buttonText: 'Coming Soon',
          buttonEnabled: false,
          route: '#',
        },
      ],
      openSkillsetModal: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isMobileScreen() {
      return window.innerWidth < 768;
    },
  },

  methods: {
    get,
    scrollCards(direction) {
      const wrapper = this.$refs.cardsWrapper;
      const currentScroll = wrapper.scrollLeft;
      if (direction === 'next') {
        wrapper.scrollLeft = currentScroll + this.scrollAmount;
      } else {
        wrapper.scrollLeft = currentScroll - this.scrollAmount;
      }
      // Update scroll position after scroll animation completes
      setTimeout(() => {
        this.updateScrollPosition();
      }, 300);
    },

    updateScrollPosition() {
      const wrapper = this.$refs.cardsWrapper;
      this.scrollPosition = wrapper.scrollLeft;
      this.isScrollEnd = wrapper.scrollLeft + wrapper.offsetWidth >= wrapper.scrollWidth - 1;
    },

    handleResize() {
      // Update scroll state when window resizes
      this.updateScrollPosition();
    },

    goToCreateSkillCard() {
      this.$router.push({ name: 'student-create-skillmap' });
    },

    goBack() {
      this.$router.go(-1);
    },
    handleModalOpen(card) {
      if (card.title === 'Skill Cards Sets') {
        this.openSkillsetModal = true;
      }
    },
  },

  mounted() {
    this.updateScrollPosition();
    this.$refs.cardsWrapper.addEventListener('scroll', this.updateScrollPosition);
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    if (this.$refs.cardsWrapper) {
      this.$refs.cardsWrapper.removeEventListener('scroll', this.updateScrollPosition);
    }
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
.content-wrapper {
  position: relative;
  max-width: 960px;
  margin: 0 auto;
  padding: 0 12px;
}

.card-scroll-area {
  position: relative;
  overflow: hidden;
}

.cards-wrapper {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 8px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.cards-wrapper::-webkit-scrollbar {
  display: none;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: white;
  border: 1px solid #ccc;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
}

.nav-button.disabled {
  opacity: 0.3;
  pointer-events: none;
}

.nav-button-left {
  left: 0;
  margin-left: -3rem; /* adjust as needed */
  margin-top: 40px;
}

.nav-button-right {
  right: 0;
  margin-right: -3rem; /* adjust as needed */
  margin-top: 40px;
}

@media (min-width: 768px) {
  .header-section,
  .card-scroll-wrapper {
    max-width: 100%;
    margin: 0 auto;
  }
}
</style>
